import type { ReactNode } from 'react';
import type { Schemas } from '@suvoda-uat/contracts';
import clsx from 'clsx';
import type { CompletionRollup } from './completion-rollup';

/** A Section of the generated Test Script (ListTestScript), straight from the contract. */
export type TestScriptSection = Schemas['Section'];

export type UATSectionProps = {
  /**
   * A real ListTestScript `Section` (F-002 US1). When supplied, the title,
   * the subsection/test-case count subtitle, and the expanded subsection list
   * are all derived from it — `title`/`subtitle`/`children` are not required.
   * Mutually exclusive in practice with the seed-data `title` path below.
   */
  section?: TestScriptSection;
  /** Section heading. Optional when `section` is supplied (its title wins). */
  title?: string;
  /** Right-aligned counts/badges shown inline with the title (e.g. "Instructive" pill). */
  titleSuffix?: ReactNode;
  /**
   * Subtitle line — e.g. "14 Subsections / 84 Test cases" or a custom
   * node (link) for newly-created sections (uat-16). Overrides the
   * auto-derived count subtitle when `section` is supplied.
   */
  subtitle?: ReactNode;
  /**
   * F-013: an interactive control slot rendered as an OVERLAY at the header's
   * right edge — a SIBLING of the toggle button, never inside it (the header
   * wraps title+subtitle in the toggle, so nested interactive elements are a
   * known gotcha). Used for the instructional include/exclude control.
   */
  headerActions?: ReactNode;
  /**
   * Feature 025 — the Section's Completion Rollup. When present, the header
   * shows a "Marked as Complete X/Y" indicator in BOTH the collapsed and open
   * states (FR-012). The orchestrator omits it for a node with zero available
   * cases (US3), so an empty/instructional/all-excluded Section shows its
   * inclusion state instead, never a misleading "0/0".
   */
  completion?: CompletionRollup;
  /** Optional column headers shown above the body (when expanded). */
  columns?: string[];
  /** Collapsed terminal card is 64px including the border in the Figma overview frame. */
  collapsedHeight?: 'default' | 'terminal';
  open?: boolean;
  onToggle?: (open: boolean) => void;
  children?: ReactNode;
};

/** Pluralise a count with its noun (e.g. 1 → "1 Subsection", 2 → "2 Subsections"). */
const pluralise = (count: number, singular: string): string =>
  `${count} ${count === 1 ? singular : `${singular}s`}`;

/** "2 Subsections / 4 Test cases" derived from a contract Section. */
const sectionSubtitle = (section: TestScriptSection): string => {
  const testCaseCount = section.subsections.reduce((sum, sub) => sum + sub.testCases.length, 0);
  return `${pluralise(section.subsections.length, 'Subsection')} / ${pluralise(testCaseCount, 'Test case')}`;
};

/**
 * The expanded body for a contract Section: one row per Subsection showing its
 * title and test-case count. Stays presentational — selecting/editing a
 * subsection is wired by the route (later slices), not here.
 */
function SectionSubsections({ section }: { section: TestScriptSection }) {
  return (
    <div data-subsection-list>
      {section.subsections.map((sub) => (
        <div
          key={sub.id}
          data-subsection-id={sub.id}
          className="flex items-center justify-between border-b border-border px-4 py-[10px] last:border-b-0"
        >
          <span className="text-xl font-semibold text-text-primary">{sub.title}</span>
          <span className="text-base text-text-secondary">
            {pluralise(sub.testCases.length, 'test case')}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Collapsible section card used on the UAT (Assembly) page. Closed = plain
 * white card; open = dark header strip to match the Figma reference frames
 * (uat-01..04).
 *
 * Two ways to feed it: the generic `title`/`subtitle`/`children` props (the
 * page-demo / seed-data path), or a real `section` from ListTestScript, in
 * which case the title, count subtitle, and subsection rows are derived for you.
 */
export function UATSection({
  section,
  title,
  titleSuffix,
  subtitle,
  headerActions,
  completion,
  columns,
  collapsedHeight = 'default',
  open = false,
  onToggle,
  children,
}: UATSectionProps) {
  const effectiveTitle = section ? section.title : (title ?? '');
  const effectiveSubtitle = subtitle ?? (section ? sectionSubtitle(section) : undefined);
  const body = section ? <SectionSubsections section={section} /> : children;

  return (
    <section
      data-open={open ? 'true' : 'false'}
      className={clsx(
        'relative rounded-md [&+&]:mt-3',
        // Collapsed: a white card with a "stacked cards" effect — two narrower
        // cards peeking ~3px and ~6px below (Figma 1061:103102). Open: the section
        // is transparent so the rounded header bar + white body card read as
        // separate blocks on the grey workspace.
        open
          ? 'border-0 bg-transparent'
          : 'overflow-hidden border border-[#f2f3f6] bg-surface-card shadow-[0_12px_0_-10px_#fff,0_12px_0_-9px_#f2f3f6,0_28px_0_-23px_#fff,0_28px_0_-22px_#f2f3f6]',
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => onToggle?.(!open)}
        className={clsx(
          // Constant gap-3 + px-4 in both states so the heading never shifts
          // horizontally when toggling; only height/background/colour change.
          'flex w-full cursor-pointer items-center gap-3 border-0 px-4 text-left',
          'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-border-focus',
          open
            ? 'h-[35px] rounded-sm py-0'
            : [collapsedHeight === 'terminal' ? 'h-[62px]' : 'h-[68px]', 'py-2'],
          open ? 'bg-[#e0e3eb] text-text-primary hover:bg-[#e0e3eb]' : 'bg-transparent hover:bg-ink-50',
        )}
      >
        <span
          className={clsx(
            // Fixed 16px box: the glyph rotates inside it, so the box footprint
            // (and therefore the heading's left edge) is identical open/closed.
            'inline-flex h-4 w-4 flex-shrink-0 items-center justify-center',
            'text-text-primary',
          )}
        >
          {/* Figma thin chevron (stroke #000212), rotates to ⌄ when open. */}
          <svg
            data-icon="chevronRight"
            width="6"
            height="9"
            viewBox="0 0 6 9"
            fill="none"
            aria-hidden="true"
            className={clsx('transition-transform duration-fast ease-standard', open && 'rotate-90')}
          >
            <path
              d="M0.493652 0.564438L4.49365 4.06444L0.493652 7.56444"
              stroke="currentColor"
              strokeWidth="1.5"
            />
          </svg>
        </span>
        <div
          className={clsx(
            'flex min-w-0 flex-1',
            open ? 'items-center gap-2' : 'flex-col gap-[2px]',
          )}
        >
          <div
            className={clsx('flex min-w-0 items-center gap-2 text-xl font-medium leading-[1.4]')}
          >
            <span className="block min-w-0 truncate">{effectiveTitle}</span>
            {titleSuffix}
          </div>
          {headerActions ? (
            // Reserve space so the overlayed control never sits on the title.
            <span aria-hidden className="w-40 flex-shrink-0" />
          ) : null}
          {effectiveSubtitle && (
            <div
              className={clsx(
                'truncate text-sm leading-[1.4] text-[#292938]/60',
                open && 'flex-shrink-0',
              )}
            >
              {effectiveSubtitle}
            </div>
          )}
        </div>
        {completion ? (
          // FR-012: a static "Marked as Complete X/Y" indicator, right-aligned in
          // the header and legible in both the collapsed (light) and open (dark)
          // states. A sibling of the title block, not part of titleSuffix (R4).
          <span
            data-section-completion
            className={clsx(
              'ml-auto flex-shrink-0 whitespace-nowrap text-sm leading-[17px] tabular-nums',
              'text-text-secondary',
            )}
          >
            Marked as Complete {completion.complete}/{completion.available}
          </span>
        ) : null}
      </button>
      {headerActions ? (
        <div
          data-section-actions
          className={clsx('absolute right-4 z-10', open ? 'top-[2px]' : 'top-1/2 -translate-y-1/2')}
        >
          {headerActions}
        </div>
      ) : null}
      {open && (
        <>
          <svg
            data-expanded-section-rail
            aria-hidden="true"
            width="13"
            height="31"
            viewBox="0 0 13 31"
            fill="none"
            className="absolute left-[18px] top-[35px] z-[1]"
          >
            <path d="M0.5 0V20C0.5 25.5228 4.97715 30 10.5 30H12.5" stroke="#91939B" />
          </svg>
          <div
            data-expanded-section-body
            className="ml-[30px] mt-[10px] overflow-hidden rounded-md border border-[#f2f3f6] bg-surface-card"
          >
            {columns && columns.length > 0 && (
              <div
                data-column-headers
                className={clsx(
                  'grid h-[33px] items-center border-b border-border bg-surface-card text-sm font-normal leading-[1.4] text-[#292938]',
                  columns.length === 3
                    ? 'grid-cols-[minmax(0,1fr)_127px_130px]'
                    : 'grid-cols-[minmax(0,1fr)_127px]',
                  // Full-height cells so the Action column's left/right dividers
                  // line up with the subsection rows below (Figma 1061-106749).
                  '[&>span]:flex [&>span]:h-full [&>span]:items-center [&>span]:whitespace-nowrap',
                  '[&>span:first-child]:pl-4 [&>span:not(:first-child)]:justify-center',
                  '[&>span:nth-child(2)]:border-x [&>span:nth-child(2)]:border-border',
                )}
              >
                {columns.map((c) => (
                  <span key={c} className="whitespace-nowrap">
                    {c}
                  </span>
                ))}
              </div>
            )}
            {/* The 1px top margin only exists to seam the body below the column
                header — drop it when there is no header (e.g. an instructive
                section's General Instructions card) so the body has no top line. */}
            {body && (
              <div data-section-body className={columns && columns.length > 0 ? 'mt-px' : undefined}>
                {body}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
